import { formatBytes, formatPercent, t } from '@glory/i18n';
import { bp } from '@glory/kernel';
import { FileText, ImageIcon, RotateCcw, Upload, X } from 'lucide-react';
import { type ClipboardEvent, useEffect, useRef, useState } from 'react';
import { type Accept, type FileRejection, useDropzone } from 'react-dropzone';

import { Button } from '../components/button.tsx';
import { cn } from '../lib/cn.ts';
import { Field, type FieldProps, useFieldControl } from './field.tsx';

/** Загруженный файл: то, что поле хранит в значении. */
export interface UploadedFile {
  readonly id: string;
  readonly name: string;
  readonly size: number;
  readonly mime: string;
  /** Ссылка на превью (картинка), если файл можно показать. */
  readonly previewUrl?: string | null;
}

/**
 * Загрузка одного файла. Поле не знает, куда грузит: приложение передаёт функцию
 * (подписанная ссылка S3 → PUT с прогрессом → подтверждение). Ошибка — Error с русским текстом.
 */
export type FileUploader = (
  file: File,
  options: { readonly onProgress: (fraction: number) => void; readonly signal: AbortSignal },
) => Promise<UploadedFile>;

/** Совпадает с лимитом модуля files (MAX_FILE_BYTES). */
const DEFAULT_MAX_SIZE = 50 * 1024 * 1024;

interface Pending {
  readonly key: string;
  readonly file: File;
  /** Превью картинки, пока она грузится (object URL, освобождается при уходе из списка). */
  readonly localUrl: string | null;
  readonly progress: number;
  readonly error: string | null;
  readonly controller: AbortController;
}

const isImage = (mime: string) => mime.startsWith('image/');

function rejectionMessage(rejection: FileRejection, maxSize: number): string {
  const code = rejection.errors[0]?.code;
  if (code === 'file-too-large') return t('errors.files.too_large', { limit: formatBytes(maxSize) });
  if (code === 'file-invalid-type') return t('fields.fileTypeNotAllowed');
  if (code === 'too-many-files') return t('fields.fileSingleOnly');
  return t('fields.fileUploadFailed');
}

let sequence = 0;
/** Ключ строки загрузки: порядковый номер в пределах страницы. */
const nextKey = () => {
  sequence += 1;
  return `upload-${String(sequence)}`;
};

function Thumbnail({ mime, url }: { mime: string; url: string | null | undefined }) {
  // Ссылка протухла или тип не отрендерился в <img> (например, octet-stream) — показываем иконку.
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  const show = typeof url === 'string' && url !== '' && url !== brokenUrl;
  return (
    <span className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-md bg-surface-2 text-muted [&_svg]:size-4">
      {show ? (
        <img
          src={url}
          alt=""
          className="size-full object-contain"
          onError={() => {
            setBrokenUrl(url);
          }}
        />
      ) : isImage(mime) ? (
        <ImageIcon aria-hidden="true" />
      ) : (
        <FileText aria-hidden="true" />
      )}
    </span>
  );
}

function PendingRow({
  item,
  onCancel,
  onRetry,
}: {
  item: Pending;
  onCancel: () => void;
  onRetry: () => void;
}) {
  const percent = formatPercent(bp(Math.round(item.progress * 10_000)));
  return (
    <li className="flex items-center gap-2.5 py-1.5">
      <Thumbnail mime={item.file.type} url={item.localUrl} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-small font-semibold">{item.file.name}</span>
          <span className="shrink-0 text-caption text-muted tabular-nums">
            {item.error ? formatBytes(item.file.size) : percent}
          </span>
        </div>
        {item.error ? (
          <span className="text-caption font-semibold text-danger">{item.error}</span>
        ) : (
          <span
            className="h-1 overflow-hidden rounded-full bg-surface-2"
            role="progressbar"
            aria-label={t('fields.fileUploading', { name: item.file.name, percent })}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(item.progress * 100)}
          >
            <span
              className="block h-full rounded-full bg-accent transition-[width] duration-200"
              style={{ width: `${String(Math.max(item.progress * 100, 2))}%` }}
            />
          </span>
        )}
      </div>
      {item.error ? (
        <Button size="sm" variant="ghost" onClick={onRetry}>
          <RotateCcw /> {t('fields.fileRetry')}
        </Button>
      ) : null}
      <button
        type="button"
        className="grid size-7 shrink-0 place-items-center rounded-sm text-muted hover:bg-surface-2 hover:text-text [&_svg]:size-4"
        aria-label={item.error ? t('fields.fileRemove') : t('fields.fileCancel')}
        title={item.error ? t('fields.fileRemove') : t('fields.fileCancel')}
        onClick={onCancel}
      >
        <X />
      </button>
    </li>
  );
}

export interface FileInputProps {
  readonly value: readonly UploadedFile[];
  readonly onValueChange: (files: UploadedFile[]) => void;
  readonly upload: FileUploader;
  /** Несколько файлов; без него новый файл заменяет прежний. */
  readonly multiple?: boolean;
  /** Допустимые типы в формате react-dropzone: `{ 'image/*': [] }`. */
  readonly accept?: Accept;
  readonly maxSize?: number;
  /** Нажатие на загруженный файл: открыть или скачать. */
  readonly onOpen?: (file: UploadedFile) => void;
  readonly disabled?: boolean | undefined;
  /** Файл отклонён до загрузки (размер, тип, количество): текст ошибки или null. */
  readonly onReject?: (message: string | null) => void;
}

/**
 * Файл: перетаскивание, выбор, вставка из буфера (⌘V в зоне), прогресс, превью картинок,
 * ошибка с повтором. Загрузка начинается сразу, значение меняется по её завершении.
 */
export function FileInput({
  value,
  onValueChange,
  upload,
  multiple = false,
  accept,
  maxSize = DEFAULT_MAX_SIZE,
  onOpen,
  disabled: disabledProp,
  onReject,
}: FileInputProps) {
  const [pending, setPending] = useState<readonly Pending[]>([]);
  const setRejected = (message: string | null) => {
    onReject?.(message);
  };
  // Актуальное значение для завершения загрузок, начатых при прежнем значении.
  const latest = useRef(value);
  useEffect(() => {
    latest.current = value;
  }, [value]);
  const control = useFieldControl();
  const disabled = disabledProp === true || control.disabled === true || control.readOnly === true;

  const update = (key: string, patch: Partial<Pending>) => {
    setPending((items) => items.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  };
  const drop = (key: string) => {
    setPending((items) =>
      items.filter((item) => {
        if (item.key !== key) return true;
        if (item.localUrl) URL.revokeObjectURL(item.localUrl);
        return false;
      }),
    );
  };

  // Уход со страницы посреди загрузки: остановить передачу и освободить превью.
  const pendingRef = useRef(pending);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);
  useEffect(
    () => () => {
      for (const item of pendingRef.current) {
        item.controller.abort();
        if (item.localUrl) URL.revokeObjectURL(item.localUrl);
      }
    },
    [],
  );

  const start = (file: File) => {
    const item: Pending = {
      key: nextKey(),
      file,
      localUrl: isImage(file.type) ? URL.createObjectURL(file) : null,
      progress: 0,
      error: null,
      controller: new AbortController(),
    };
    setPending((items) => {
      if (!multiple) {
        for (const previous of items) {
          previous.controller.abort();
          if (previous.localUrl) URL.revokeObjectURL(previous.localUrl);
        }
      }
      return [...(multiple ? items : []), item];
    });
    run(item);
  };

  const run = (item: Pending) => {
    upload(item.file, {
      signal: item.controller.signal,
      onProgress: (fraction) => {
        update(item.key, { progress: Math.min(Math.max(fraction, 0), 1) });
      },
    }).then(
      (uploaded) => {
        drop(item.key);
        onValueChange(multiple ? [...latest.current, uploaded] : [uploaded]);
      },
      (error: unknown) => {
        if (item.controller.signal.aborted) return;
        update(item.key, {
          error:
            error instanceof Error && error.message !== '' ? error.message : t('fields.fileUploadFailed'),
        });
      },
    );
  };

  const retry = (item: Pending) => {
    const next: Pending = { ...item, progress: 0, error: null, controller: new AbortController() };
    setPending((items) => items.map((i) => (i.key === item.key ? next : i)));
    run(next);
  };

  const cancel = (item: Pending) => {
    item.controller.abort();
    drop(item.key);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    multiple,
    maxSize,
    disabled,
    noClick: true,
    ...(accept ? { accept } : {}),
    onDrop: (accepted, rejections) => {
      setRejected(rejections[0] ? rejectionMessage(rejections[0], maxSize) : null);
      for (const file of accepted) start(file);
    },
  });

  const onPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const files = [...event.clipboardData.files];
    if (files.length === 0 || disabled) return;
    event.preventDefault();
    const allowed = multiple ? files : files.slice(0, 1);
    const tooBig = allowed.find((f) => f.size > maxSize);
    setRejected(tooBig ? t('errors.files.too_large', { limit: formatBytes(maxSize) }) : null);
    for (const file of allowed) if (file.size <= maxSize) start(file);
  };

  const showZone = multiple || (value.length === 0 && pending.length === 0);
  // react-dropzone отдаёт ref зоны через getRootProps — так устроен его API; значение ref при рендере не читается.
  // eslint-disable-next-line react-hooks/refs -- см. комментарий выше
  const rootProps = getRootProps({
    onPaste,
    className: cn(
      'flex flex-col items-center gap-2 rounded-md border-thick border-dashed border-border px-4 py-5 text-center',
      'transition-colors outline-none focus-visible:border-focus',
      isDragActive && 'border-accent bg-selected',
      disabled && 'cursor-not-allowed opacity-60',
    ),
  });

  return (
    <div className="flex flex-col gap-1.5">
      {showZone ? (
        <div {...rootProps}>
          <input {...getInputProps({ id: control.id })} />
          <Upload className="size-5 text-muted" aria-hidden="true" />
          <p className="text-small text-muted">
            {isDragActive
              ? t('fields.fileDropActive')
              : multiple
                ? t('fields.fileDropMany')
                : t('fields.fileDrop')}
          </p>
          <Button size="sm" onClick={open} disabled={disabled} aria-describedby={control['aria-describedby']}>
            {multiple ? t('fields.fileChooseMany') : t('fields.fileChoose')}
          </Button>
          <p className="text-caption text-muted">{t('fields.fileLimit', { limit: formatBytes(maxSize) })}</p>
        </div>
      ) : null}
      {value.length > 0 || pending.length > 0 ? (
        <ul className="flex flex-col divide-y divide-border-soft rounded-md border-thick border-border bg-surface px-2.5">
          {value.map((file) => (
            <li key={file.id} className="flex items-center gap-2.5 py-1.5">
              <Thumbnail mime={file.mime} url={file.previewUrl} />
              <div className="flex min-w-0 flex-1 flex-col">
                {onOpen ? (
                  <button
                    type="button"
                    className="truncate text-left text-small font-semibold hover:underline"
                    title={t('fields.fileOpen', { name: file.name })}
                    onClick={() => {
                      onOpen(file);
                    }}
                  >
                    {file.name}
                  </button>
                ) : (
                  <span className="truncate text-small font-semibold">{file.name}</span>
                )}
                <span className="text-caption text-muted tabular-nums">{formatBytes(file.size)}</span>
              </div>
              {!multiple && !disabled ? (
                <Button size="sm" variant="ghost" onClick={open}>
                  {t('fields.fileReplace')}
                </Button>
              ) : null}
              {disabled ? null : (
                <button
                  type="button"
                  className="grid size-7 shrink-0 place-items-center rounded-sm text-muted hover:bg-surface-2 hover:text-text [&_svg]:size-4"
                  aria-label={t('fields.fileRemove')}
                  title={t('fields.fileRemove')}
                  onClick={() => {
                    onValueChange(value.filter((f) => f.id !== file.id));
                  }}
                >
                  <X />
                </button>
              )}
            </li>
          ))}
          {pending.map((item) => (
            <PendingRow
              key={item.key}
              item={item}
              onCancel={() => {
                cancel(item);
              }}
              onRetry={() => {
                retry(item);
              }}
            />
          ))}
        </ul>
      ) : null}
      {/* Скрытый ввод для замены файла в режиме одного файла, когда зона не показана. */}
      {showZone ? null : <input {...getInputProps()} />}
    </div>
  );
}

export interface FileFieldProps
  extends Omit<FieldProps, 'children'>, Omit<FileInputProps, 'disabled' | 'onReject'> {}

/** Поле «Файл» с подписью, подсказкой и ошибкой; отклонённый файл показывается как ошибка поля. */
export function FileField({
  value,
  onValueChange,
  upload,
  multiple,
  accept,
  maxSize,
  onOpen,
  ...field
}: FileFieldProps) {
  const [rejected, setRejected] = useState<string | null>(null);
  return (
    <Field {...field} error={field.error ?? rejected}>
      <FileInput
        value={value}
        onValueChange={onValueChange}
        upload={upload}
        onReject={setRejected}
        {...(multiple === undefined ? {} : { multiple })}
        {...(accept === undefined ? {} : { accept })}
        {...(maxSize === undefined ? {} : { maxSize })}
        {...(onOpen === undefined ? {} : { onOpen })}
      />
    </Field>
  );
}
